/**
 * TextInputBar — text mode input with Send button, image attachment, and document upload.
 *
 * Enabled only when sessionStore.mode === 'gary_speaking'.
 * Enter key submits. Clears input after send.
 * Image upload: up to 4 photos per message, JPEG/PNG/WebP only (Claude Vision constraint).
 * Document upload: .txt and .pdf, up to 50,000 characters extracted client-side.
 */

import { useState, useRef, type FormEvent, type KeyboardEvent } from 'react';
import { requestUploadUrl, uploadPhotoToS3, pollPhotoReady } from '../../lib/photoUpload';
import { extractDocumentText, ALLOWED_DOC_EXTENSIONS, type ExtractedDocument } from '../../lib/documentExtract';

export interface PendingPhoto {
  localId: string;
  photoId: string | null;
  previewUrl: string;
  uploading: boolean;
  error?: string;
}

interface TextInputBarProps {
  onSend: (text: string, photoIds?: string[], mediaPreviews?: string[], doc?: { content: string; filename: string }) => void;
  disabled: boolean;
}

export const CHAT_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_CHAT_PHOTOS = 4;

let nextLocalId = 0;
export function newLocalPhotoId(): string {
  nextLocalId += 1;
  return `local-${nextLocalId}`;
}

export async function uploadChatPhoto(
  file: File,
  update: (patch: Partial<PendingPhoto>) => void,
): Promise<void> {
  try {
    const { uploadUrl, photoId } = await requestUploadUrl(file.name, file.type);
    await uploadPhotoToS3(uploadUrl, file);
    await pollPhotoReady(photoId, 500, 10000);
    update({ photoId, uploading: false });
  } catch {
    update({ uploading: false, error: 'Upload failed' });
  }
}

export function TextInputBar({ onSend, disabled }: TextInputBarProps) {
  const [value, setValue] = useState('');
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [pendingDoc, setPendingDoc] = useState<ExtractedDocument | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);

  const anyUploading = pendingPhotos.some(p => p.uploading);
  const readyPhotos = pendingPhotos.filter(p => p.photoId !== null);
  const photoLimitReached = pendingPhotos.length >= MAX_CHAT_PHOTOS;
  const canSend =
    !disabled &&
    !anyUploading &&
    (value.trim().length > 0 || readyPhotos.length > 0 || pendingDoc !== null);

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSend) return;

    const text = value.trim();
    const photoIds = readyPhotos.length ? readyPhotos.map(p => p.photoId as string) : undefined;
    const mediaPreviews = readyPhotos.length ? readyPhotos.map(p => p.previewUrl) : undefined;
    const doc = pendingDoc ? { content: pendingDoc.text, filename: pendingDoc.filename } : undefined;

    onSend(text, photoIds, mediaPreviews, doc);
    setValue('');
    setPendingPhotos([]);
    setPendingDoc(null);
    setDocError(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;

    const room = MAX_CHAT_PHOTOS - pendingPhotos.length;
    for (const file of files.slice(0, Math.max(room, 0))) {
      const localId = newLocalPhotoId();
      if (!CHAT_IMAGE_TYPES.includes(file.type)) {
        setPendingPhotos(prev => [...prev, { localId, photoId: null, previewUrl: '', uploading: false, error: 'Only JPEG, PNG, and WebP are supported' }]);
        continue;
      }
      const previewUrl = URL.createObjectURL(file);
      setPendingPhotos(prev => [...prev, { localId, photoId: null, previewUrl, uploading: true }]);
      void uploadChatPhoto(file, (patch) => {
        setPendingPhotos(prev => prev.map(p => p.localId === localId ? { ...p, ...patch } : p));
      });
    }
  };

  const handleDocChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setDocError(null);

    try {
      const extracted = await extractDocumentText(file);
      setPendingDoc(extracted);
    } catch {
      setDocError('Could not read this file. Try a .txt or .pdf.');
    }
  };

  const removePhoto = (localId: string) => {
    setPendingPhotos(prev => {
      const target = prev.find(p => p.localId === localId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(p => p.localId !== localId);
    });
  };

  const removeDoc = () => setPendingDoc(null);

  const attachButtonClass = (extra?: boolean) => [
    'flex-none w-10 h-10 rounded-xl flex items-center justify-center transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-amber-400',
    disabled || extra
      ? 'text-stone-300 cursor-not-allowed'
      : 'text-stone-500 hover:text-stone-800 hover:bg-stone-100',
  ].join(' ');

  return (
    <div className="border-t border-stone-200 bg-white">
      {/* Attachment previews */}
      {(pendingPhotos.length > 0 || pendingDoc || docError) && (
        <div className="px-4 pt-3 flex items-start gap-3 flex-wrap">

          {/* Image previews */}
          {pendingPhotos.map(photo => (
            photo.error ? (
              <div key={photo.localId} className="flex items-center gap-2">
                <p className="text-xs text-red-600">{photo.error}</p>
                <button type="button" onClick={() => removePhoto(photo.localId)} className="text-xs text-stone-500 underline">Dismiss</button>
              </div>
            ) : (
              <div key={photo.localId} className="relative inline-block">
                <img
                  src={photo.previewUrl}
                  alt="Attachment preview"
                  className="h-16 w-16 rounded-lg object-cover border border-stone-200"
                />
                {photo.uploading ? (
                  <div className="absolute inset-0 rounded-lg bg-black/40 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  </div>
                ) : (
                  <button type="button" onClick={() => removePhoto(photo.localId)} aria-label="Remove image"
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-stone-700 text-white flex items-center justify-center hover:bg-stone-900">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            )
          ))}

          {/* Document chip */}
          {pendingDoc && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-sky-50 border border-sky-200 text-sky-800 text-xs max-w-xs">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 flex-none" aria-hidden="true">
                <path fillRule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625zM7.5 15a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5A.75.75 0 017.5 15zm.75-6.75a.75.75 0 000 1.5H12a.75.75 0 000-1.5H8.25z" clipRule="evenodd" />
                <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
              </svg>
              <span className="truncate">{pendingDoc.filename}</span>
              <span className="flex-none text-sky-500">{(pendingDoc.charCount / 1000).toFixed(1)}k chars</span>
              {pendingDoc.truncated && <span className="flex-none text-amber-600">truncated</span>}
              <button type="button" onClick={removeDoc} aria-label="Remove document"
                className="flex-none ml-1 text-sky-400 hover:text-sky-700">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}

          {/* Document error */}
          {docError && (
            <div className="flex items-center gap-2">
              <p className="text-xs text-red-600">{docError}</p>
              <button type="button" onClick={() => setDocError(null)} className="text-xs text-stone-500 underline">Dismiss</button>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-end gap-2 px-4 py-3">
        {/* Hidden inputs */}
        <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple
          className="sr-only" aria-label="Attach images"
          onChange={handleImageChange} disabled={disabled || photoLimitReached} />
        <input ref={docInputRef} type="file" accept=".txt,.pdf"
          className="sr-only" aria-label="Attach document"
          onChange={handleDocChange} disabled={disabled || pendingDoc !== null} />

        {/* Image button */}
        <button type="button" onClick={() => imageInputRef.current?.click()}
          disabled={disabled || photoLimitReached} aria-label="Attach images"
          className={attachButtonClass(photoLimitReached)}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
            <path fillRule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clipRule="evenodd" />
          </svg>
        </button>

        {/* Document button */}
        <button type="button" onClick={() => docInputRef.current?.click()}
          disabled={disabled || pendingDoc !== null} aria-label={`Attach document (${ALLOWED_DOC_EXTENSIONS.join(', ')})`}
          className={attachButtonClass(pendingDoc !== null)}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
            <path fillRule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625zM7.5 15a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5A.75.75 0 017.5 15zm.75-6.75a.75.75 0 000 1.5H12a.75.75 0 000-1.5H8.25z" clipRule="evenodd" />
            <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
          </svg>
        </button>

        <label htmlFor="text-input" className="sr-only">Your message</label>
        <textarea
          id="text-input" rows={1} value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown} disabled={disabled}
          placeholder={disabled ? 'Waiting for AI response…' : 'Type your answer…'}
          className={[
            'flex-1 resize-none rounded-xl border px-4 py-3 text-sm leading-relaxed',
            'focus:outline-none focus:ring-2 focus:ring-amber-400 max-h-40 overflow-y-auto',
            disabled
              ? 'border-stone-200 bg-stone-50 text-stone-400 cursor-not-allowed'
              : 'border-stone-300 bg-white text-stone-900',
          ].join(' ')}
          aria-describedby="text-input-hint"
        />
        <span id="text-input-hint" className="sr-only">Press Enter to send, Shift+Enter for new line</span>

        <button type="submit" disabled={!canSend} aria-label="Send message"
          className={[
            'flex-none w-11 h-11 rounded-xl flex items-center justify-center transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-amber-400',
            !canSend ? 'bg-stone-200 text-stone-400 cursor-not-allowed' : 'bg-stone-800 text-white hover:bg-stone-700',
          ].join(' ')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
            <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
          </svg>
        </button>
      </form>
    </div>
  );
}
